/**
 * @file TitlePicker.tsx
 * @brief Courtesy title selection — pill trigger + searchable modal sheet.
 *
 * @details
 * Mirrors mobile `VZTitlePicker` and panel picker conventions (count in
 * title, pill rows, optional clear). Stores the canonical **label**
 * (e.g. "Mr.") so existing backend `users.title` strings keep working.
 *
 * @author DES
 * @date 19-06-2026
 */

import { useMemo, useState } from "react";
import { ChevronsUpDown, Type } from "lucide-react";
import { TitleBadge } from "@/components/ui/TitleBadge";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { TITLES, findTitle, type TitleOption } from "@/shared/data/titles";
import { cn } from "@/shared/theme/cn";

interface TitlePickerProps {
  /** Stored value — title label (e.g. "Mr.") or legacy free text. */
  value?: string | null;
  onChange: (titleLabel: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  clearable?: boolean;
  sheetTitle?: string;
  sheetSubtitle?: string;
}

interface TitlePickerItem extends PickerSheetItem {
  title: TitleOption;
}

export function TitlePicker({
  value,
  onChange,
  placeholder = "Select title…",
  disabled,
  size = "md",
  clearable = true,
  sheetTitle,
  sheetSubtitle = "Courtesy title for profile and correspondence.",
}: TitlePickerProps) {
  const [open, setOpen] = useState(false);

  const matched = useMemo(() => findTitle(value), [value]);
  const triggerLabel = matched?.label ?? (value?.trim() ? value.trim() : null);

  const items: TitlePickerItem[] = useMemo(
    () =>
      TITLES.map((title) => ({
        id: title.code,
        label: title.label,
        searchKeys: [title.code],
        title,
      })),
    [],
  );

  const modalTitle = sheetTitle ?? `Select title (${TITLES.length})`;
  const triggerHeight = size === "sm" ? "h-9" : "h-10";

  return (
    <>
      <PickerSheet<TitlePickerItem>
        anchor={
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex w-full items-center justify-between rounded-full border border-input bg-field px-5 text-sm transition-colors",
              triggerHeight,
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              !triggerLabel && "text-muted-foreground",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              {matched || triggerLabel ? (
                <TitleBadge value={value} title={matched} size="sm" />
              ) : (
                <Type className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
              )}
              <span className="truncate">{triggerLabel ?? placeholder}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </button>
        }
        open={open}
        onOpenChange={setOpen}
        title={modalTitle}
        subtitle={sheetSubtitle}
        searchPlaceholder="Search titles…"
        items={items}
        selectedId={matched?.code ?? null}
        onSelect={(item) => onChange(item.title.label)}
        onClear={clearable ? () => onChange(null) : undefined}
        clearable={clearable}
        renderLeftAccessory={(item) => <TitleBadge title={item.title} size="lg" />}
      />
    </>
  );
}
